import React from 'react';
import { Box, Typography, useTheme, useMediaQuery, IconButton } from '@mui/material';
import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { colors } from '../../../themes/typography';
import { responsiveTypography, responsiveSpacing } from '../../../themes/responsive';
import SelectableAlertCard from '../../../components/shared/SelectableAlertCard';
import AlertCardCollectionProvider from '../../../components/shared/AlertCardCollectionProvider';

const AlertColumn = ({ 
  column, 
  onAlertAction, 
  loading, 
  itemsPerPage = 10, 
  clientId,
  onDataRefresh,
  onOperationSuccess,
  onOperationError
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  // Pagination state
  const [currentPage, setCurrentPage] = React.useState(0);
  
  // Reset pagination when alerts change
  React.useEffect(() => {
    setCurrentPage(0);
  }, [column.alerts]);

  // Pagination calculations
  const totalAlerts = column.alerts?.length || 0;
  const totalPages = Math.ceil(totalAlerts / itemsPerPage);
  const startIndex = currentPage * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedAlerts = column.alerts?.slice(startIndex, endIndex) || [];

  // Pagination handlers
  const handlePreviousPage = () => {
    setCurrentPage(prev => Math.max(0, prev - 1));
  };

  const handleNextPage = () => {
    setCurrentPage(prev => Math.min(totalPages - 1, prev + 1));
  };
  
  // Navigation handler for machine ID clicks
  const handleMachineIdClick = (alert) => {
    if (alert.group && alert.location) {
      navigate('/machines', {
        state: {
          defaultTab: 'planograma',
          filters: {
            group: alert.group,
            location: alert.location
          }
        }
      });
    }
  };
  
  return (
    <Box 
      sx={{
        flex: isMobile ? 'none' : '1 1 0', // Equal distribution for 3 columns
        width: isMobile ? '100%' : 'auto', // Auto width for flex items
        minWidth: isMobile ? '100%' : '216px', // Increased by 10% from previous (196 * 1.1)
        maxWidth: isMobile ? '100%' : '308px', // Increased by 10% from previous (280 * 1.1)
        backgroundColor: colors.background.surface,
        borderRadius: '12px',
        border: `2px solid ${colors.border.light}`,
        display: 'flex',
        flexDirection: 'column',
        height: isMobile ? 'auto' : 'auto',
        minHeight: isMobile ? 'auto' : '100vh',
        boxShadow: `0 2px 8px ${colors.shadow.soft}`,
        transition: 'all 0.2s ease',
        mb: isMobile ? responsiveSpacing.md : 0,
        '&:hover': {
          borderColor: column.color,
          boxShadow: `0 4px 16px ${colors.shadow.soft}`,
        }
      }}
    >
      {/* Column Header */}
      <Box sx={{
        p: responsiveSpacing.md,
        borderBottom: `2px solid ${colors.border.light}`,
        backgroundColor: column.color,
        borderRadius: '10px 10px 0 0',
        position: 'relative',
        '&::after': {
          content: '""',
          position: 'absolute',
          bottom: '-2px',
          left: 0,
          right: 0,
          height: '2px',
          backgroundColor: column.color,
        }
      }}>
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <Typography sx={{
            ...responsiveTypography.h4,
            fontWeight: 700,
            color: 'white',
            textShadow: '0 1px 2px rgba(0,0,0,0.2)'
          }}>
            {column.title}
          </Typography>
          <Typography sx={{
            ...responsiveTypography.caption,
            color: 'white',
            backgroundColor: 'rgba(255, 255, 255, 0.2)',
            px: '8px',
            py: '2px',
            borderRadius: '12px',
            fontWeight: 700,
            minWidth: '20px',
            textAlign: 'center',
            backdropFilter: 'blur(4px)'
          }}>
            {totalAlerts}
          </Typography>
        </Box>
      </Box>

      {/* Column Content */}
      <Box 
        sx={{
          flex: 1,
          p: responsiveSpacing.md,
          display: 'flex',
          flexDirection: 'column',
          gap: responsiveSpacing.md,
          overflowY: 'visible',
          minHeight: 'auto',
          // Custom scrollbar for individual columns (when needed)
          '&::-webkit-scrollbar': {
            width: '6px',
          },
          '&::-webkit-scrollbar-track': {
            backgroundColor: colors.background.subtle,
            borderRadius: '3px',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: colors.border.medium,
            borderRadius: '3px',
            '&:hover': {
              backgroundColor: column.color,
            },
          },
        }}
      >
        {totalAlerts === 0 ? (
          <Typography sx={{ 
            ...responsiveTypography.body,
            fontWeight: 500,
            color: colors.text.secondary,
            textAlign: 'center',
            py: responsiveSpacing.md
          }}>
            {t('noAlertsFound') || 'No alerts found'}
          </Typography>
        ) : (
          <AlertCardCollectionProvider
            onAlertAction={onAlertAction}
            onDataRefresh={onDataRefresh}
            onOperationSuccess={onOperationSuccess}
            onOperationError={onOperationError}
            clientId={clientId}
          >
            {paginatedAlerts.map((alert) => (
              <SelectableAlertCard
                key={alert.id}
                item={alert}
                alertKey={`${alert.id}`}
                clientId={clientId}
                showMachineId={true}
                showBadge={false}
                handleMachineIdClick={handleMachineIdClick}
                onStatusChange={onAlertAction}
                t={t}
              />
            ))}
          </AlertCardCollectionProvider>
        )}
        
        {/* Pagination Controls */}
        {totalPages > 1 && (
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            p: responsiveSpacing.sm,
            borderTop: `1px solid ${colors.border.light}`,
            backgroundColor: 'rgba(255, 255, 255, 0.5)',
            mt: 'auto' // Push to bottom
          }}>
            <IconButton
              onClick={handlePreviousPage}
              disabled={currentPage === 0}
              size="small"
              sx={{
                backgroundColor: currentPage === 0 ? 'transparent' : colors.background.surface,
                color: currentPage === 0 ? colors.text.secondary : colors.text.primary,
                '&:hover': {
                  backgroundColor: currentPage === 0 ? 'transparent' : colors.background.subtle,
                },
                '&:disabled': {
                  opacity: 0.4
                }
              }}
            >
              <ChevronLeft fontSize="small" />
            </IconButton>
            
            <Typography sx={{
              ...responsiveTypography.small,
              fontWeight: 600,
              color: colors.text.primary,
              minWidth: '60px',
              textAlign: 'center'
            }}>
              {startIndex + 1}-{Math.min(endIndex, totalAlerts)} of {totalAlerts}
            </Typography>
            
            <IconButton
              onClick={handleNextPage}
              disabled={currentPage === totalPages - 1}
              size="small"
              sx={{
                backgroundColor: currentPage === totalPages - 1 ? 'transparent' : colors.background.surface,
                color: currentPage === totalPages - 1 ? colors.text.secondary : colors.text.primary,
                '&:hover': {
                  backgroundColor: currentPage === totalPages - 1 ? 'transparent' : colors.background.subtle,
                },
                '&:disabled': {
                  opacity: 0.4
                }
              }}
            >
              <ChevronRight fontSize="small" />
            </IconButton>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default AlertColumn;
